import {
  COCO_DATASET_CREATE_TOOL_KEY,
  COCO_DATASET_EDIT_TOOL_KEY,
  COCO_DATASET_READ_TOOL_KEY,
  COCO_DATASET_WRITE_TOOL_KEY,
  COCO_PROMOTE_DATASET_TOOL_KEY,
  COCO_RUN_DATASET_WORKFLOWS_TOOL_KEY,
} from '../coco/coco-tool-keys';

/**
 * Tool keys are `namespace/name`, and for nearly every tool the namespace also
 * names the provider the catalog groups under — `slack/send-message` belongs to
 * `slack`. The `coco/` namespace is the exception: it marks a tool as builtin
 * to Coco rather than as belonging to a "coco" provider, so the prefix says
 * nothing about where the tool belongs.
 *
 * These four declare `ProviderKey.Dataset` on their classes and are the dataset
 * editing tools that sit alongside `dataset/query/execute`. Grouping by the key
 * prefix instead filed them under the catch-all Cotera group, which is what the
 * `provider` field on each class was already trying (and failing) to say.
 *
 * Kept honest by `provider-for-tool-key.spec.ts` on the server, which is the
 * only place that can see both this map and the classes' `provider` fields.
 */
const TOOL_KEY_PROVIDER_OVERRIDES: Readonly<Record<string, string>> = {
  [COCO_DATASET_CREATE_TOOL_KEY]: 'dataset',
  [COCO_DATASET_READ_TOOL_KEY]: 'dataset',
  [COCO_DATASET_WRITE_TOOL_KEY]: 'dataset',
  [COCO_DATASET_EDIT_TOOL_KEY]: 'dataset',
  // Runs a dataset's automations from chat; it acts on a dataset and belongs
  // beside the other dataset tools, not in the catch-all Cotera group its
  // `coco/` prefix would otherwise land it in.
  [COCO_RUN_DATASET_WORKFLOWS_TOOL_KEY]: 'dataset',
  // Saves a chat-scoped uploaded dataset into the library; a dataset action, so
  // it belongs with the other dataset tools rather than the catch-all Cotera
  // group its `coco/` prefix would land it in.
  [COCO_PROMOTE_DATASET_TOOL_KEY]: 'dataset',
};

/**
 * The provider a tool key belongs to in the tools catalog.
 *
 * Use this rather than splitting the key by hand: it is what the server filters
 * `GET /tools-v2/providers/:provider/tools` on, what the client groups the
 * catalog by, and what the provider icon and deep links resolve through — all
 * four have to agree or a tool lands in a group its own page cannot open.
 */
export const providerForToolKey = (toolKey: string): string => {
  const override = TOOL_KEY_PROVIDER_OVERRIDES[toolKey];
  if (override !== undefined) {
    return override;
  }
  const firstSlash = toolKey.indexOf('/');
  return firstSlash > 0 ? toolKey.substring(0, firstSlash) : toolKey;
};
